import { Grid, Box } from '@mui/material'
import { Brand, Position, State } from 'src/types'
import { genBaseCells } from 'src/features/generatePdf/helper/glove'
import { genColorCellsByPosition } from 'src/features/generatePdf/helper'
import { useLocale } from 'src/locales/useLocale'
import { RagLionLogoConfirm } from './RagLionLogo'
import { Embroideries } from 'src/features/simulationConfirm/components/Embroideries'
import { ItemCell } from '../components/ItemCell'
import { muiGrey } from 'src/style/muiTheme'
import { AdditionalEmbroideryConfirm } from './AdditionalEmbroideryConfirm'
import { RagLogoConfirm } from './RagLogo'

export const cellColor = (cellValue: string) => (cellValue === 'unselected' ? 'red' : muiGrey)

type Props = {
  state: State
  brand: Brand
  position: Position
}

export const SimulationContent: React.FC<Props> = ({ state, brand, position }) => {
  const { t } = useLocale()
  const baseCells = genBaseCells(state, brand, position, t)
  const colorCells = genColorCellsByPosition(position, state, brand, t)
  //  ONLY FOR RAGDELION
  const isRagDeLion = brand === 'ragdelion'
  const isRagLionLogo = isRagDeLion && state?.ragLionLogo?.value !== 'none'
  const indexOfRagLionLogo = isRagDeLion ? 1 : 0
  const indexOfRagLionLogoColor = isRagLionLogo ? 2 : indexOfRagLionLogo

  const baseCellCount = baseCells.length
  const colorCellCount = colorCells.length
  const baseAndColorCellCount = baseCellCount + colorCellCount

  // ONLY FOR YELLSTORY
  const isYellStory = brand === 'yellStory'
  const indexOfYsrOriginalEmbroidery = isYellStory ? 1 : 0
  const ysrOriginalLogoIndex = baseAndColorCellCount + indexOfYsrOriginalEmbroidery
  const ysrDirectLogo1PositionIndex = ysrOriginalLogoIndex + 1
  const ysrDirectLogo1MainColorIndex = ysrDirectLogo1PositionIndex + (state.ysrDirectLogo1Position?.value !== 'none' ? 1 : 0)
  const ysrDirectLogo1EdgeColorIndex = ysrDirectLogo1PositionIndex + (state.ysrDirectLogo1Position?.value !== 'none' ? 2 : 0)
  const ysrDirectLogo2PositionIndex = ysrDirectLogo1EdgeColorIndex + 1
  const ysrDirectLogo2ColorIndex = ysrDirectLogo2PositionIndex + (state.ysrDirectLogo2Position?.value !== 'none' ? 1 : 0)

  const optionalIndex = isYellStory ? indexOfYsrOriginalEmbroidery : isRagDeLion ? indexOfRagLionLogoColor : 0
  const ragLionLogoColorIndex = baseAndColorCellCount + optionalIndex

  const isSelectableRagLogo = isRagDeLion && state.leatherIntegratedMiddle?.value === 'atMiddleFinger'
  const ragLogoIndex = isSelectableRagLogo ? ragLionLogoColorIndex + 1 : 0
  const ragLogoColorIndex = isSelectableRagLogo ? ragLogoIndex + 1 : 0

  return (
    <Grid container>
      <Grid item xs={12} sm={4}>
        <Box pt={2}>
          <Box fontWeight="bold" fontSize="16px" color={muiGrey}>
            {t.BASE_ITEMS}
          </Box>
          {baseCells?.map((cell, i) => <ItemCell key={cell.head} itemIndex={i + 1} cell={cell} color={cellColor(cell.value)} />)}
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box pt={2}>
          <Box fontWeight="bold" fontSize="16px" color={muiGrey}>
            {t.COLOR_ITEMS}
          </Box>
          {colorCells?.map((cell, i) => <ItemCell key={cell.head} itemIndex={i + baseCellCount + 1} cell={cell} color={cellColor(cell.value)} />)}
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box pt={2}>
          <RagLionLogoConfirm
            {...{ isRagDeLion, isRagLionLogo }}
            logoLabel={state.ragLionLogo?.label}
            logoColorLabel={state.ragLionLogoColor?.label}
            logoIndex={baseAndColorCellCount + indexOfRagLionLogo}
            logoColorIndex={ragLionLogoColorIndex}
          />
          <RagLogoConfirm
            isDisplay={isRagDeLion && isSelectableRagLogo}
            isRagLogo={state.ragLogo?.value !== 'none'}
            logoIndex={ragLogoIndex}
            logoColorIndex={ragLogoColorIndex}
            logoLabel={state.ragLogo?.label}
            logoColorLabel={state.ragLogoColor?.label}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory}
            index={ysrOriginalLogoIndex}
            header={'オリジナル刺繍'}
            targetState={state.ysrOriginalEmbroidery}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory}
            index={ysrDirectLogo1PositionIndex}
            header={'ダイレクトロゴ刺繍1'}
            targetState={state.ysrDirectLogo1Position}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory && !['unselected', 'none'].includes(state.ysrDirectLogo1Position?.value)}
            index={ysrDirectLogo1MainColorIndex}
            item={'本体カラー'}
            targetState={state.ysrDirectLogo1MainColor}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory && !['unselected', 'none'].includes(state.ysrDirectLogo1Position?.value)}
            index={ysrDirectLogo1EdgeColorIndex}
            item={'フチカラー'}
            targetState={state.ysrDirectLogo1EdgeColor}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory}
            index={ysrDirectLogo2PositionIndex}
            header={'ダイレクトロゴ刺繍2'}
            targetState={state.ysrDirectLogo2Position}
          />
          <AdditionalEmbroideryConfirm
            isDisplay={isYellStory && !['unselected', 'none'].includes(state.ysrDirectLogo2Position?.value)}
            index={ysrDirectLogo2ColorIndex}
            item={'カラー'}
            targetState={state.ysrDirectLogo2Color}
          />
          <Box mt={2}>
            <Embroideries embroideries={state.embroideries} previousIndex={(isYellStory ? ysrDirectLogo2ColorIndex : ragLogoColorIndex) + 1} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
